<template>
    <div class="comentarios-ticket">
        <v-divider class="my-4"></v-divider>
        <h3 class="mb-4">Comentarios</h3>

        <!-- Lista de comentarios -->
        <div v-if="comentarios.length > 0" class="comentarios-list mb-4">
            <v-card
                v-for="comentario in comentarios"
                :key="comentario.id"
                class="mb-3"
                outlined
            >
                <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-2">
                        <div class="d-flex align-center">
                            <v-avatar size="32" class="mr-2">
                                <v-icon>mdi-account-circle</v-icon>
                            </v-avatar>
                            <div>
                                <div class="font-weight-bold">
                                    {{ comentario.usuario?.nombre || 'Usuario' }}
                                </div>
                                <div class="text-caption text--secondary">
                                    {{ formatDate(comentario.created_at) }}
                                </div>
                            </div>
                        </div>
                        <div v-if="canEdit(comentario)" class="d-flex">
                            <v-btn
                                icon
                                small
                                @click="editComentario(comentario)"
                                v-if="!editingId || editingId !== comentario.id"
                            >
                                <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                small
                                @click="confirmDelete(comentario.id)"
                            >
                                <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <!-- Vista de comentario -->
                    <div
                        v-if="editingId !== comentario.id"
                        class="comentario-content"
                        v-html="comentario.comentario"
                    ></div>

                    <!-- Edición de comentario -->
                    <div v-else>
                        <RichTextComponent
                            v-model="editComentarioText"
                            uploadUrl="api/ticket/image"
                            :fixedToolbar="false"
                        ></RichTextComponent>
                        <ArchivosComponentVue
                            v-model="editArchivos"
                        ></ArchivosComponentVue>
                        <div class="mt-2">
                            <v-btn
                                small
                                color="success"
                                class="white--text mr-2"
                                @click="saveEdit(comentario.id)"
                            >
                                Guardar
                            </v-btn>
                            <v-btn
                                small
                                outlined
                                @click="cancelEdit"
                            >
                                Cancelar
                            </v-btn>
                        </div>
                    </div>

                    <!-- Archivos adjuntos -->
                    <div
                        v-if="comentario.archivos && comentario.archivos.length > 0"
                        class="mt-3"
                    >
                        <v-chip
                            v-for="archivo in comentario.archivos"
                            :key="archivo.id"
                            small
                            class="mr-2 mb-2"
                            @click="downloadFile(archivo)"
                            style="cursor: pointer"
                        >
                            <v-icon small left>mdi-paperclip</v-icon>
                            {{ archivo.file_name }}
                        </v-chip>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Formulario para nuevo comentario -->
        <v-card outlined>
            <v-card-text>
                <h4 class="mb-3">Agregar comentario</h4>
                <RichTextComponent
                    v-model="nuevoComentario"
                    uploadUrl="api/ticket/image"
                    :fixedToolbar="false"
                ></RichTextComponent>
                <ArchivosComponentVue
                    v-model="nuevosArchivos"
                ></ArchivosComponentVue>
                <div class="mt-3">
                    <v-btn
                        color="success"
                        class="white--text"
                        @click="guardarComentario"
                        :loading="loading"
                        :disabled="loading || !nuevoComentario || nuevoComentario.trim() === ''"
                    >
                        <v-icon left>mdi-send</v-icon>
                        Enviar comentario
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>

        <!-- Dialog de confirmación de eliminación -->
        <v-dialog v-model="deleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="text-h5 text-center">
                    ¿Confirmar eliminación?
                </v-card-title>
                <v-card-text class="text-center">
                    ¿Estás seguro de que deseas eliminar este comentario? Esta
                    acción no se puede deshacer.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="deleteDialog = false">
                        Cancelar
                    </v-btn>
                    <v-btn color="red" text @click="deleteComentario">
                        Eliminar
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import RichTextComponent from '../../modulos/tickets/componentes/RichTextComponent.vue';
import ArchivosComponentVue from '../../modulos/tickets/componentes/ArchivosComponent.vue';
import { mapGetters } from 'vuex';

export default {
    components: {
        RichTextComponent,
        ArchivosComponentVue,
    },
    props: {
        ticketId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            comentarios: [],
            nuevoComentario: '',
            nuevosArchivos: [],
            loading: false,
            editingId: null,
            editComentarioText: '',
            editArchivos: [],
            deleteDialog: false,
            comentarioToDelete: null,
        };
    },
    mounted() {
        this.cargarComentarios();
        // Marcar comentarios como leídos al abrir
        this.marcarComentariosLeidos();
    },
    watch: {
        ticketId() {
            if (this.ticketId) {
                this.cargarComentarios();
            }
        },
    },
    methods: {
        async cargarComentarios() {
            if (!this.ticketId) return;

            this.loading = true;
            try {
                const response = await axios.get(
                    `api/tickets/${this.ticketId}/comentarios`
                );
                this.comentarios = response.data.success || [];
            } catch (error) {
                this.$toast.error('Error cargando comentarios');
            } finally {
                this.loading = false;
            }
        },

        async guardarComentario() {
            if (!this.nuevoComentario || this.nuevoComentario.trim() === '') {
                return;
            }

            this.loading = true;
            try {
                const formData = {
                    id_ticket: this.ticketId,
                    comentario: this.nuevoComentario,
                    archivos: this.nuevosArchivos,
                };

                const response = await axios.post(
                    'api/tickets/comentarios',
                    formData
                );

                this.$toast.sucs('Comentario agregado exitosamente');
                this.nuevoComentario = '';
                this.nuevosArchivos = [];
                this.cargarComentarios();
            } catch (error) {
                this.$toast.error('Error guardando comentario');
            } finally {
                this.loading = false;
            }
        },

        editComentario(comentario) {
            this.editingId = comentario.id;
            this.editComentarioText = comentario.comentario;
            // Convertir archivos del comentario al formato esperado
            this.editArchivos = comentario.archivos
                ? comentario.archivos.map((archivo) => ({
                      nombre: archivo.file_name,
                      archivo: `storage/ticket/${archivo.url}`,
                  }))
                : [];
        },

        async saveEdit(comentarioId) {
            this.loading = true;
            try {
                const formData = {
                    comentario: this.editComentarioText,
                    archivos: this.editArchivos,
                };

                await axios.put(`api/tickets/comentarios/${comentarioId}`, formData);

                this.$toast.sucs('Comentario actualizado exitosamente');
                this.cancelEdit();
                this.cargarComentarios();
            } catch (error) {
                this.$toast.error('Error actualizando comentario');
            } finally {
                this.loading = false;
            }
        },

        cancelEdit() {
            this.editingId = null;
            this.editComentarioText = '';
            this.editArchivos = [];
        },

        confirmDelete(comentarioId) {
            this.comentarioToDelete = comentarioId;
            this.deleteDialog = true;
        },

        async deleteComentario() {
            if (!this.comentarioToDelete) return;

            this.loading = true;
            try {
                await axios.delete(
                    `api/tickets/comentarios/${this.comentarioToDelete}`
                );

                this.$toast.sucs('Comentario eliminado exitosamente');
                this.deleteDialog = false;
                this.comentarioToDelete = null;
                this.cargarComentarios();
            } catch (error) {
                this.$toast.error('Error eliminando comentario');
            } finally {
                this.loading = false;
            }
        },

        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },

        canEdit(comentario) {
            const currentUser = this.userData;
            return (
                currentUser &&
                comentario.id_usuario === currentUser.id
            );
        },

        downloadFile(archivo) {
            const fileUrl = `storage/ticket/${archivo.url}`;
            window.open(fileUrl, '_blank');
        },

        async marcarComentariosLeidos() {
            if (!this.ticketId) return;
            try {
                await axios.post(`api/tickets/${this.ticketId}/comentarios/marcar-leidos`);
                // Emitir evento para actualizar el kanban
                this.$emit('comentarios-leidos');
            } catch (error) {
                // Silenciar errores
            }
        },
    },
    computed: {
        ...mapGetters({
            userData: 'auth/userData',
        }),
    },
};
</script>

<style scoped>
.comentarios-ticket {
    margin-top: 20px;
}

.comentario-content {
    margin-top: 10px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 4px;
}

.comentario-content >>> img {
    max-width: 100%;
    height: auto;
}
</style>

